/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/components/shadcn/dialog';
import type { EventRecurrenceScope } from '@services/requests/housien-models/CalendarEventInput';
import { Label, UnstyledButton, NativeRadio } from '@/shared/ui';

interface RecurrenceScopeDialogProps {
  open: boolean;
  action: 'edit' | 'delete';
  onConfirm: (scope: EventRecurrenceScope) => void;
  onClose: () => void;
}

const SCOPES: EventRecurrenceScope[] = ['this', 'following', 'all'];

const SCOPE_LABEL_KEY: Record<EventRecurrenceScope, string> = {
  this: 'this_only',
  following: 'this_and_following',
  all: 'all',
};

/**
 * Asks which occurrences a recurring change applies to.
 *
 * @param props Open flag, action kind and handlers.
 * @returns Rendered scope dialog.
 */
export const RecurrenceScopeDialog: React.FC<RecurrenceScopeDialogProps> = ({
  open,
  action,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation('features/agenda');
  const [scope, setScope] = useState<EventRecurrenceScope>('this');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t(`scope.title_${action}`)}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-(--space-2)">
          {SCOPES.map((s) => (
            <Label
              key={s}
              className="flex cursor-pointer items-center gap-(--space-2) rounded-(--radius-md) border border-[var(--border-subtle)] p-(--space-2-5) text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)]"
              style={{ fontSize: 'var(--text-small)' }}
            >
              <NativeRadio
                
                name="recurrence-scope"
                checked={scope === s}
                onChange={() => setScope(s)}
              />
              {t(`scope.${SCOPE_LABEL_KEY[s]}`)}
            </Label>
          ))}
        </div>
        <DialogFooter>
          <UnstyledButton
            type="button"
            onClick={onClose}
            className="rounded-(--radius-md) border border-[var(--border-subtle)] px-(--space-5) py-(--space-2) text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
            style={{ fontSize: 'var(--text-small)' }}
          >
            {t('actions.cancel')}
          </UnstyledButton>
          <UnstyledButton
            type="button"
            onClick={() => onConfirm(scope)}
            className="rounded-(--radius-md) px-(--space-5) py-(--space-2) font-semibold text-[var(--button-primary-text)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
            style={{ background: 'var(--seed-base)', fontSize: 'var(--text-small)' }}
          >
            {t('actions.confirm')}
          </UnstyledButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
